import React, { useState, useEffect, useMemo } from "react";
import { useAuth } from "@/lib/AuthContext";
import { entities, getDisplayMessage } from "@/api/apiClient";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { format } from "date-fns";
import { Search } from "lucide-react";
import SelecionarPregaoModal from "@/components/propostas/SelecionarPregaoModal";

export default function EventoFormDialog({ open, onOpenChange, evento, selectedDate }) {
  const queryClient = useQueryClient();
  
  const { data: editais = [] } = useQuery({
    queryKey: ["editais-select-agenda"],
    queryFn: () => entities.Edital.list("-createdAt", 500),
    enabled: open,
  });

  const { data: users = [] } = useQuery({
    queryKey: ["users-select"],
    queryFn: () => entities.User.list(), // O tratamento de erro 403 já está no entities.User.list()
    enabled: open,
    retry: false, // Não tentar novamente
    onError: (error) => {
      // Não logar erros 403 ou silenciosos - eles já são tratados internamente
      if (error?.status !== 403 && !error?.isForbidden && !error?.silent) {
        console.error('Erro ao carregar usuários:', error);
      }
    },
  });

  const { data: companies = [] } = useQuery({
    queryKey: ["companies-select"],
    queryFn: () => entities.Company.list(),
    enabled: open,
  });

  const [formData, setFormData] = useState({
    titulo: "",
    descricao: "",
    tipo_evento: "Outros",
    responsavel_email: "",
    data_inicio: "",
    hora_inicio: "09:00",
    data_fim: "",
    hora_fim: "10:00",
    dia_inteiro: false,
    local: "",
    edital_id: "",
    status: "Agendado",
    lembrete_minutos: 60,
    compartilhar_com_equipe: false,
  });

  const [modalPregaoAberto, setModalPregaoAberto] = useState(false);

  useEffect(() => {
    if (!open) setModalPregaoAberto(false);
  }, [open]);

  useEffect(() => {
    if (evento) {
      // Validar e converter data de início
      let dataInicio = null;
      if (evento.data_inicio || evento.dataInicio) {
        const dateStr = evento.data_inicio || evento.dataInicio;
        const date = new Date(dateStr);
        if (!isNaN(date.getTime())) {
          dataInicio = date;
        } else {
          console.error('Data de início inválida:', dateStr);
        }
      }
      
      // Validar e converter data de fim
      let dataFim = null;
      if (evento.data_fim || evento.dataFim) {
        const dateStr = evento.data_fim || evento.dataFim;
        const date = new Date(dateStr);
        if (!isNaN(date.getTime())) {
          dataFim = date;
        } else {
          console.error('Data de fim inválida:', dateStr);
        }
      }
      
      setFormData({
        titulo: evento.titulo || "",
        descricao: evento.descricao || "",
        tipo_evento: evento.tipo_evento || "Outros",
        responsavel_email: evento.responsavel_email || "",
        data_inicio: dataInicio ? format(dataInicio, "yyyy-MM-dd") : "",
        hora_inicio: dataInicio ? format(dataInicio, "HH:mm") : "09:00",
        data_fim: dataFim ? format(dataFim, "yyyy-MM-dd") : "",
        hora_fim: dataFim ? format(dataFim, "HH:mm") : "10:00",
        status: evento.status || "Agendado",
        dia_inteiro: evento.dia_inteiro || false,
        local: evento.local || "",
        edital_id: evento.edital_id || evento.editalId || "",
        lembrete_minutos: evento.lembrete_minutos || 60,
        compartilhar_com_equipe: evento.compartilhar_com_equipe || false,
      });
    } else if (selectedDate && open) {
      // Validar selectedDate antes de formatar
      if (selectedDate instanceof Date && !isNaN(selectedDate.getTime())) {
        setFormData(prev => ({
          ...prev,
          data_inicio: format(selectedDate, "yyyy-MM-dd"),
          data_fim: format(selectedDate, "yyyy-MM-dd"),
        }));
      }
    }
  }, [evento, selectedDate, open]);

  const createMutation = useMutation({
    mutationFn: (data) => entities.AgendaEvent.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries(["agenda-eventos"]);
      toast.success("Evento criado com sucesso!", {
        description: "O evento foi adicionado à agenda e estará visível para você e sua equipe.",
        duration: 5000,
      });
      onOpenChange(false);
      resetForm();
    },
    onError: (error) => {
      toast.error(getDisplayMessage(error, "Erro ao criar evento. Tente novamente."), { duration: 6000 });
      if (!error?.isConnectionError) console.error(error);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => entities.AgendaEvent.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(["agenda-eventos"]);
      toast.success("Evento atualizado com sucesso!", {
        description: "As alterações foram salvas e já estão visíveis na agenda.",
        duration: 5000,
      });
      onOpenChange(false);
      resetForm();
    },
    onError: (error) => {
      toast.error(getDisplayMessage(error, "Erro ao atualizar evento. Tente novamente."), { duration: 6000 });
      if (!error?.isConnectionError) console.error(error);
    },
  });

  const resetForm = () => {
    setFormData({
      titulo: "",
      descricao: "",
      tipo_evento: "Outros",
      responsavel_email: "",
      data_inicio: "",
      hora_inicio: "09:00",
      data_fim: "",
      hora_fim: "10:00",
      dia_inteiro: false,
      local: "",
      edital_id: "",
      status: "Agendado",
      lembrete_minutos: 60,
      compartilhar_com_equipe: false,
    });
  };

  const { user: currentUser, isLoadingAuth: isLoadingUser } = useAuth();

  // Garantir que o usuário atual sempre esteja disponível na lista de responsáveis
  const availableUsers = useMemo(() => {
    const usersList = users || [];
    // Se não houver usuários (erro 403) mas houver usuário atual, adicionar ele
    if (usersList.length === 0 && currentUser?.email) {
      return [{
        id: currentUser.id || 'current',
        email: currentUser.email,
        nome: currentUser.nome || currentUser.full_name || currentUser.email,
        full_name: currentUser.full_name || currentUser.nome || currentUser.email
      }];
    }
    return usersList;
  }, [users, currentUser]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.titulo || !formData.data_inicio) {
      toast.error("Preencha os campos obrigatórios");
      return;
    }

    if (!currentUser) {
      toast.error("Carregando dados do usuário...");
      return;
    }

    // Tentar usar company_id do usuário, ou primeira empresa disponível
    let companyId = currentUser?.company_id ?? currentUser?.empresaId;
    if (!companyId && companies.length > 0) {
      companyId = companies[0].id;
    }

    if (!companyId) {
      toast.error("Nenhuma empresa cadastrada. Crie uma empresa primeiro na página de Empresas.");
      return;
    }

    const dataHoraInicio = `${formData.data_inicio}T${formData.hora_inicio}:00`;
    const dataHoraFim = formData.data_fim && formData.hora_fim ? `${formData.data_fim}T${formData.hora_fim}:00` : null;

    const responsavel = availableUsers.find(u => u.email === formData.responsavel_email);

    const eventData = {
      titulo: formData.titulo,
      descricao: formData.descricao,
      tipo_evento: formData.tipo_evento,
      responsavel_email: formData.responsavel_email && formData.responsavel_email !== "none" ? formData.responsavel_email : null,
      responsavel_nome: responsavel?.nome || responsavel?.full_name || null,
      data_inicio: dataHoraInicio,
      data_fim: dataHoraFim,
      dia_inteiro: formData.dia_inteiro,
      local: formData.local,
      edital_id: formData.edital_id && formData.edital_id !== "none" ? formData.edital_id : null,
      status: formData.status,
      lembrete_minutos: formData.lembrete_minutos,
      compartilhar_com_equipe: formData.compartilhar_com_equipe,
      company_id: companyId,
    };

    if (evento) {
      updateMutation.mutate({ id: evento.id, data: eventData });
    } else {
      createMutation.mutate(eventData);
    }
  };

  const handleClose = () => {
    onOpenChange(false);
    if (!evento) {
      resetForm();
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{evento ? "Editar Evento" : "Novo Evento"}</DialogTitle>
          <DialogDescription className="sr-only">
            {evento ? "Formulário para editar os dados do evento da agenda." : "Formulário para criar um novo evento na agenda."}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Título */}
          <div className="space-y-2">
            <Label htmlFor="titulo">
              Título <span className="text-red-500">*</span>
            </Label>
            <Input
              id="titulo"
              value={formData.titulo}
              onChange={(e) => setFormData({ ...formData, titulo: e.target.value })}
              placeholder="Ex: Reunião com cliente"
              required
            />
          </div>

          {/* Tipo de Evento */}
          <div className="space-y-2">
            <Label htmlFor="tipo_evento">Tipo de Evento</Label>
            <Select value={formData.tipo_evento || "Outros"} onValueChange={(value) => setFormData({ ...formData, tipo_evento: value })}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Retorno Pregão">Retorno Pregão</SelectItem>
                <SelectItem value="Visita Cliente">Visita Cliente</SelectItem>
                <SelectItem value="Reunião">Reunião</SelectItem>
                <SelectItem value="Entrega Proposta">Entrega Proposta</SelectItem>
                <SelectItem value="Abertura Licitação">Abertura Licitação</SelectItem>
                <SelectItem value="Outros">Outros</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Responsável */}
          <div className="space-y-2">
            <Label htmlFor="responsavel">Responsável</Label>
            <Select 
              value={formData.responsavel_email || "none"} 
              onValueChange={(value) => setFormData({ ...formData, responsavel_email: value === "none" ? "" : value })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Selecione um responsável" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Nenhum</SelectItem>
                {availableUsers.length > 0 ? (
                  availableUsers.map((user) => (
                    <SelectItem key={user.id || user.email} value={user.email}>
                      {user.full_name || user.nome || user.email}
                    </SelectItem>
                  ))
                ) : (
                  currentUser?.email && (
                    <SelectItem value={currentUser.email}>
                      {currentUser.full_name || currentUser.nome || currentUser.email} (Você)
                    </SelectItem>
                  )
                )}
              </SelectContent>
            </Select>
            {availableUsers.length === 0 && currentUser && (
              <p className="text-xs text-slate-500 mt-1">
                Apenas você está disponível como responsável
              </p>
            )}
          </div>

          {/* Dia inteiro */}
          <div className="flex items-center space-x-2">
            <Switch
              id="dia_inteiro"
              checked={formData.dia_inteiro}
              onCheckedChange={(checked) => setFormData({ ...formData, dia_inteiro: checked })}
            />
            <Label htmlFor="dia_inteiro">Evento de dia inteiro</Label>
          </div>

          {/* Data e hora */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="data_inicio">
                Data de Início <span className="text-red-500">*</span>
              </Label>
              <Input
                id="data_inicio"
                type="date"
                value={formData.data_inicio}
                onChange={(e) => setFormData({ ...formData, data_inicio: e.target.value })}
                required
              />
            </div>
            {!formData.dia_inteiro && (
              <div className="space-y-2">
                <Label htmlFor="hora_inicio">Hora de Início</Label>
                <Input
                  id="hora_inicio"
                  type="time"
                  value={formData.hora_inicio}
                  onChange={(e) => setFormData({ ...formData, hora_inicio: e.target.value })}
                />
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="data_fim">Data de Término</Label>
              <Input
                id="data_fim"
                type="date"
                value={formData.data_fim}
                onChange={(e) => setFormData({ ...formData, data_fim: e.target.value })}
              />
            </div>
            {!formData.dia_inteiro && (
              <div className="space-y-2">
                <Label htmlFor="hora_fim">Hora de Término</Label>
                <Input
                  id="hora_fim"
                  type="time"
                  value={formData.hora_fim}
                  onChange={(e) => setFormData({ ...formData, hora_fim: e.target.value })}
                />
              </div>
            )}
          </div>

          {/* Descrição */}
          <div className="space-y-2">
            <Label htmlFor="descricao">Descrição</Label>
            <Textarea
              id="descricao"
              value={formData.descricao}
              onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
              placeholder="Detalhes do evento"
              rows={3}
            />
          </div>

          {/* Local */}
          <div className="space-y-2">
            <Label htmlFor="local">Local</Label>
            <Input
              id="local"
              value={formData.local}
              onChange={(e) => setFormData({ ...formData, local: e.target.value })}
              placeholder="Ex: Sala de reuniões, endereço do cliente"
            />
          </div>

          {/* Edital Relacionado — consultar e selecionar pregão */}
          <div className="space-y-2">
            <Label htmlFor="edital-relacionado">Edital Relacionado (opcional)</Label>
            <div className="flex flex-col sm:flex-row gap-2">
              <div
                id="edital-relacionado"
                className="flex-1 rounded-lg border border-input bg-muted/40 px-3 py-2 text-sm text-slate-700 min-h-[40px] flex items-center"
              >
                {formData.edital_id
                  ? (() => {
                      const edital = (Array.isArray(editais) ? editais : []).find((e) => e.id === formData.edital_id);
                      if (!edital) return `Pregão vinculado (ID: ${formData.edital_id.slice(0, 8)}…)`;
                      const inst = edital.instituicao || edital.titulo_licitacao || edital.nome_arquivo || "";
                      const pe = edital.numero_pregao ? ` • ${edital.numero_pregao}` : "";
                      const proc = edital.numero_processo ? ` • ${edital.numero_processo}` : "";
                      return inst ? `${inst}${pe}${proc}` : `Pregão (ID: ${edital.id.slice(0, 8)}…)`;
                    })()
                  : "Nenhum pregão selecionado — clique em Consultar pregão"}
              </div>
              <div className="flex gap-2 shrink-0">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setModalPregaoAberto(true)}
                  className="gap-2"
                >
                  <Search className="w-4 h-4" />
                  Consultar pregão
                </Button>
                {formData.edital_id ? (
                  <Button
                    type="button"
                    variant="ghost"
                    className="text-slate-600"
                    onClick={() => setFormData((prev) => ({ ...prev, edital_id: "" }))}
                  >
                    Remover
                  </Button>
                ) : null}
              </div>
            </div>
            <SelecionarPregaoModal
              open={modalPregaoAberto}
              onOpenChange={setModalPregaoAberto}
              editais={editais}
              editalSelecionadoId={formData.edital_id || null}
              onSelecionar={(edital) => setFormData((prev) => ({ ...prev, edital_id: edital.id }))}
              statusFiltro="todos"
              textoAjuda='Busque por instituição, número do pregão, processo ou objeto e clique em "Selecionar" para vincular ao evento da agenda.'
            />
          </div>

          {/* Status */}
          <div className="space-y-2">
            <Label htmlFor="status">Status</Label>
            <Select value={formData.status || "Agendado"} onValueChange={(value) => setFormData({ ...formData, status: value })}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Agendado">Agendado</SelectItem>
                <SelectItem value="Em Andamento">Em Andamento</SelectItem>
                <SelectItem value="Concluído">Concluído</SelectItem>
                <SelectItem value="Cancelado">Cancelado</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Lembrete */}
          <div className="space-y-2">
            <Label htmlFor="lembrete">Lembrete (minutos antes)</Label>
            <Select
              value={String(formData.lembrete_minutos)}
              onValueChange={(value) => setFormData({ ...formData, lembrete_minutos: parseInt(value) })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="15">15 minutos</SelectItem>
                <SelectItem value="30">30 minutos</SelectItem>
                <SelectItem value="60">1 hora</SelectItem>
                <SelectItem value="120">2 horas</SelectItem>
                <SelectItem value="1440">1 dia</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Compartilhar com equipe */}
          <div className="flex items-center space-x-2 p-4 bg-blue-50 rounded-lg">
            <Switch
              id="compartilhar"
              checked={formData.compartilhar_com_equipe}
              onCheckedChange={(checked) => setFormData({ ...formData, compartilhar_com_equipe: checked })}
            />
            <Label htmlFor="compartilhar" className="cursor-pointer">
              Compartilhar com toda a equipe da empresa
            </Label>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleClose}>
              Cancelar
            </Button>
            <Button type="submit" disabled={createMutation.isPending || updateMutation.isPending || isLoadingUser}>
              {(createMutation.isPending || updateMutation.isPending) && "Salvando..."}
              {isLoadingUser && "Carregando..."}
              {!(createMutation.isPending || updateMutation.isPending || isLoadingUser) && (evento ? "Atualizar" : "Criar")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}